import { useS3Download } from '@doltech/core/lib/hooks/useS3Download';
import { IconOtherBack15S } from '@doltech/icons/IconOtherBack15S';
import { IconOtherNext15S } from '@doltech/icons/IconOtherNext15S';
import { IconOtherPause } from '@doltech/icons/IconOtherPause';
import { IconOtherPlay } from '@doltech/icons/IconOtherPlay';
import {
  FlatAudioPlayer,
  FlatPlayerInstance,
} from '@doltech/ui/lib/figma/Audio/Player/FlatAudioPlayer';
import { Button } from '@doltech/ui/lib/figma/ButtonV2/ButtonV2';
import { Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { useTestSubmissionStore } from '../../../model/test-submissions.hook';
import { PracticeTestBottomNavigationListening } from './PracticeTestBottomNavigationListening';
import { PracticeTestQuestionBar } from './PracticeTestQuestionBar';

const Main = styled.div`
  position: relative;
  .audio-player-wrapper {
    position: absolute;
    left: 0;
    right: 0;
    top: -10px;
    z-index: 23;
  }
`;

const AudioActions = styled.div`
  margin-left: 8px;
  display: flex;
  align-items: center;
  button {
    margin-right: 8px;
    &:last-child {
      margin-right: 0;
    }
  }
`;

interface PracticeTestListeningBottomNavigationProps {
  onSubmitWithEvents?: () => void;
}

export const PracticeTestListeningBottomNavigation = observer(
  (props: PracticeTestListeningBottomNavigationProps) => {
    const { onSubmitWithEvents, ...rest } = props;
    const store = useTestSubmissionStore();
    const [isPlaying, setIsPlaying] = React.useState(false);
    const s3AudioData = useS3Download(store.currentTestSection?.script?.audio?.path);
    const audioPlayerRef = React.useRef<FlatPlayerInstance>();

    React.useEffect(() => {
      if (audioPlayerRef.current) {
        store.registerAudioPlayerRef(audioPlayerRef.current);
      }
      if (audioPlayerRef.current && store.listeningGuideConfirmModalVisible) {
        audioPlayerRef.current.pause();
      }
    }, [store, store.listeningGuideConfirmModalVisible]);

    React.useEffect(() => {
      if (store.isStartedDoingTest) {
        audioPlayerRef?.current?.startPlaying();
        store.startSection();
      }
    }, [store.isStartedDoingTest, store]);

    return (
      <Main {...rest}>
        <div className="audio-player-wrapper">
          <Spin size="small" spinning={s3AudioData.isLoading}>
            <FlatAudioPlayer
              key={store.currentSectionIndex}
              ref={audioPlayerRef}
              volume={1}
              url={s3AudioData.data?.data?.downloadResourceUrl}
              onPause={() => {
                setIsPlaying(false);
              }}
              onPlay={() => {
                setIsPlaying(true);
              }}
            />
          </Spin>
        </div>
        <PracticeTestBottomNavigationListening
          leftArea={
            <AudioActions className="audio-actions">
              <Button.Icon
                onClick={() => {
                  if (store.preventUserAction()) return;
                  audioPlayerRef.current.rewind15Second();
                }}
              >
                <IconOtherBack15S />
              </Button.Icon>
              <Button.Icon
                onClick={() => {
                  if (store.preventUserAction()) return;
                  audioPlayerRef.current.playPause();
                }}
              >
                {isPlaying ? <IconOtherPause /> : <IconOtherPlay />}
              </Button.Icon>
              <Button.Icon>
                <IconOtherNext15S
                  onClick={() => {
                    if (store.preventUserAction()) return;
                    audioPlayerRef.current.forward15Second();
                  }}
                />
              </Button.Icon>
            </AudioActions>
          }
          middleArea={<PracticeTestQuestionBar />}
          onSubmitWithEvents={onSubmitWithEvents}
        />
      </Main>
    );
  }
);
